import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Empty, ErrorMsg, Loading, PageHead, Section } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import type {
  PublicationDetail,
  PublicationFile,
  PublicationKind,
  PublicationStatus,
  PublishingAccount,
  TextSuggestion,
} from '../../lib/contentTypes';
import { uploadPublicationFiles } from './common';
import { CopyButton, KIND_LABEL, PUB_STATUS_LABEL } from './Publications';
import { PublicationVideos } from './Videos';

interface Draft {
  text: string;
  tags: string;
  language: string;
  url: string;
  at: string; // YYYY-MM-DD
  version: number | null; // null: not saved yet
  dirty: boolean;
  msg: string;
  err: string;
  instruction: string;
}

const parseTags = (s: string) => s.split(/[\s,]+/).filter(Boolean);
const chars = (s: string) => Array.from(s).length;
const MAX_TEXT = 10_000;
const ACCEPT =
  'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,audio/mpeg,audio/wav,audio/mp4,application/pdf,.mp4,.mov,.m4a,.webm';

function toDrafts(d: PublicationDetail): Record<string, Draft> {
  const out: Record<string, Draft> = {};
  for (const t of d.texts)
    out[t.account_id] = {
      text: t.text,
      tags: t.hashtags.join(' '),
      language: t.language,
      url: t.published_url ?? '',
      at: t.published_at ? t.published_at.slice(0, 10) : '',
      version: t.version,
      dirty: false,
      msg: '',
      err: '',
      instruction: '',
    };
  return out;
}

export function PublicationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [maxFileMb, setMaxFileMb] = useState(2000);
  const [d, setD] = useState<PublicationDetail | null>(null);
  const [f, setF] = useState({ title: '', kind: 'post', status: 'planned', planned_for: '', output_folder: '', notes: '' });
  const [items, setItems] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [progress, setProgress] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(
    (initial: boolean) =>
      api<PublicationDetail>(`/api/content/publications/${id}`)
        .then((x) => {
          setD(x);
          if (initial) {
            setF({
              title: x.title,
              kind: x.kind,
              status: x.status,
              planned_for: x.planned_for ?? '',
              output_folder: x.output_folder ?? '',
              notes: x.notes ?? '',
            });
            setItems(x.content_item_ids);
            setDrafts(toDrafts(x));
          }
        })
        .catch((e) => setError(errorText(e))),
    [id],
  );
  useEffect(() => {
    void load(true);
    api<{ max_file_mb: number }>('/api/content/meta/publications')
      .then((m) => setMaxFileMb(m.max_file_mb))
      .catch(() => {});
  }, [load]);

  const reloadFiles = useCallback(() => void load(false), [load]);
  const waiting = d?.jobs.some((j) => j.status === 'queued' || j.status === 'running') ?? false;
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => void load(false), 3000);
    return () => clearInterval(t);
  }, [waiting, load]);

  if (error && !d) return <ErrorMsg>{error}</ErrorMsg>;
  if (!d) return <Loading what="Loading the publication" />;

  const setDraft = (acc: string, patch: Partial<Draft>) => setDrafts((all) => ({ ...all, [acc]: { ...all[acc], ...patch } }));

  async function saveDetails(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMsg('');
    try {
      await api(`/api/content/publications/${id}`, {
        method: 'PATCH',
        body: {
          title: f.title,
          kind: f.kind,
          status: f.status,
          planned_for: f.planned_for || null,
          output_folder: f.output_folder || null,
          notes: f.notes || null,
          content_item_ids: items,
          version: d!.version,
        },
      });
      setMsg('Publication saved.');
      await load(false);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setError('');
    const { done, errors } = await uploadPublicationFiles(Array.from(files), d!.id, maxFileMb, setProgress);
    setMsg(`${done.length} file${done.length === 1 ? '' : 's'} added${errors.length ? '' : '.'}`);
    if (errors.length) setError(errors.join('; '));
    await load(false);
  }
  async function removeFile(file: PublicationFile) {
    try {
      await api(`/api/content/publications/${id}/files/${file.id}`, { method: 'DELETE' });
      await load(false);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function saveText(acc: string) {
    const x = drafts[acc];
    setDraft(acc, { err: '', msg: '' });
    try {
      const t = await api<{ version: number }>(`/api/content/publications/${id}/texts/${acc}`, {
        method: 'PUT',
        body: {
          text: x.text,
          hashtags: parseTags(x.tags),
          language: x.language,
          published_url: x.url || null,
          published_at: x.at ? `${x.at}T12:00:00Z` : null,
          version: x.version,
        },
      });
      setDraft(acc, { version: t.version, dirty: false, msg: 'Saved.' });
      await load(false);
    } catch (err) {
      setDraft(acc, { err: errorText(err) });
    }
  }
  async function removeText(acc: string) {
    const x = drafts[acc];
    if (x.version !== null) {
      try {
        await api(`/api/content/publications/${id}/texts/${acc}`, { method: 'DELETE' });
      } catch (err) {
        setDraft(acc, { err: errorText(err) });
        return;
      }
    }
    setDrafts((all) => {
      const { [acc]: _gone, ...rest } = all;
      return rest;
    });
    await load(false);
  }
  async function suggest(acc: string) {
    const x = drafts[acc];
    setDraft(acc, { err: '', msg: '' });
    try {
      await api(`/api/content/publications/${id}/suggestions`, {
        method: 'POST',
        body: { account_id: acc, language: x.language, instruction: x.instruction.trim() || null },
        idempotencyKey: newIdempotencyKey(),
      });
      await load(false);
    } catch (err) {
      setDraft(acc, { err: errorText(err) });
    }
  }
  function applySuggestion(acc: string, s: TextSuggestion) {
    setDraft(acc, { text: s.text, tags: s.hashtags.join(' '), language: s.language, dirty: true, msg: 'The suggestion is in the text box. Save the text to keep it.' });
  }
  async function deletePublication() {
    try {
      await api(`/api/content/publications/${id}`, { method: 'DELETE' });
      navigate(`/content/projects/${d!.project_id}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  const accountOf = (acc: string): PublishingAccount => d.accounts.find((a) => a.id === acc) ?? { id: acc, label: `${acc} (removed account)`, channel: 'other', kind: 'company' };
  const order = (acc: string) => {
    const i = d.accounts.findIndex((x) => x.id === acc);
    return i < 0 ? 999 : i;
  };
  const shown = Object.keys(drafts).sort((a, b) => order(a) - order(b));
  const free = d.accounts.filter((a) => !(a.id in drafts));

  return (
    <div className="page">
      <p className="crumbs small">
        <Link to="/content?tab=publications">Content Studio · Publications</Link> · <Link to={`/content/projects/${d.project_id}`}>{d.project.title}</Link>
      </p>
      <PageHead title={d.title} meta={`${KIND_LABEL[d.kind]} · ${PUB_STATUS_LABEL[d.status]}${d.planned_for ? ` · planned ${d.planned_for}` : ''}`} />

      <form className="form-grid" onSubmit={saveDetails} aria-label="Publication details">
        <label className="field">
          <span>Title</span>
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required maxLength={300} />
        </label>
        <div className="row gap">
          <label className="field grow">
            <span>Kind</span>
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              {(Object.keys(KIND_LABEL) as PublicationKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="field grow">
            <span>Status</span>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              {(Object.keys(PUB_STATUS_LABEL) as PublicationStatus[]).map((s) => (
                <option key={s} value={s}>
                  {PUB_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>Planned for</span>
          <input type="date" value={f.planned_for} onChange={(e) => setF({ ...f, planned_for: e.target.value })} />
        </label>
        <div className="field">
          <span>Output folder (a local path or a cloud link, kept as you type it)</span>
          <div className="row gap center">
            <input
              className="grow"
              value={f.output_folder}
              onChange={(e) => setF({ ...f, output_folder: e.target.value })}
              maxLength={1000}
              placeholder="D:\Renders\project\short-01  or  https://drive.example.com/…"
              aria-label="Output folder"
            />
            {f.output_folder && <CopyButton value={f.output_folder} label="Copy folder" />}
            {d.output_folder_is_link && d.output_folder === f.output_folder && (
              <a className="small" href={d.output_folder ?? '#'} target="_blank" rel="noreferrer noopener">
                Open link
              </a>
            )}
          </div>
        </div>
        <label className="field span2">
          <span>Notes</span>
          <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={5000} />
        </label>
        <fieldset className="field span2">
          <legend>Content items this publication uses (from this project)</legend>
          {d.project_items.length === 0 ? (
            <p className="muted small">This project has no content item yet.</p>
          ) : (
            <ul className="plain">
              {d.project_items.map((i) => (
                <li key={i.id} className="row gap center">
                  <label className="check">
                    <input type="checkbox" checked={items.includes(i.id)} onChange={(e) => setItems(e.target.checked ? [...items, i.id] : items.filter((x) => x !== i.id))} /> {i.title}
                  </label>
                  <Link className="small" to={`/content/items/${i.id}`}>
                    Open in the editor
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </fieldset>
        <div className="span2 row gap center">
          <button type="submit" className="btn primary">
            Save publication
          </button>
          {msg && (
            <span className="ok-text small" role="status">
              {msg}
            </span>
          )}
        </div>
        <div className="span2">
          <ErrorMsg>{error}</ErrorMsg>
        </div>
      </form>

      <Section
        title={`Files (${d.files.length})`}
        aside={
          <label className="btn file-btn small">
            Add files
            <input type="file" accept={ACCEPT} multiple onChange={(e) => { void onFiles(e.target.files); e.target.value = ''; }} aria-label="Add publication files" />
          </label>
        }
      >
        <p className="muted small">
          The final files of this publication: images, videos up to {maxFileMb / 1000} GB, audio and PDF. They are kept as they are and checked by their content, not their name. Removing one only unlinks it from this publication.
        </p>
        {progress && (
          <p className="small" role="status">
            {progress}
          </p>
        )}
        {d.files.length === 0 ? (
          <Empty>No file yet.</Empty>
        ) : (
          <ul className="plain cs-pubfiles">
            {d.files.map((file) => (
              <li key={file.id} className="cs-pubfile" data-kind={file.kind}>
                {file.kind === 'image' && <img src={file.url} alt={file.filename} loading="lazy" />}
                {file.kind === 'video' && <video src={file.url} controls preload="metadata" aria-label={`Video ${file.filename}`} />}
                {file.kind === 'audio' && <audio src={file.url} controls preload="metadata" aria-label={`Audio ${file.filename}`} />}
                {file.kind === 'pdf' && (
                  <a href={file.url} target="_blank" rel="noreferrer noopener">
                    Open the PDF
                  </a>
                )}
                <div className="cs-pubfile-meta">
                  <span className="cs-asset-name" title={file.filename}>
                    {file.filename}
                  </span>
                  <span className="muted small num">
                    {file.mime} · {(file.bytes / 1_000_000).toFixed(1)} MB
                  </span>
                  <span className="row gap">
                    <a className="small" href={file.download_url}>
                      Download
                    </a>
                    <button type="button" className="btn ghost small" onClick={() => void removeFile(file)}>
                      Remove
                    </button>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <PublicationVideos
        publicationId={d.id}
        projectId={d.project_id}
        projectTitle={d.project.title}
        projectLocation={d.project.location ?? null}
        onFilms={reloadFiles}
      />

      <Section
        title={`Texts per account (${shown.length})`}
        aside={
          free.length > 0 && (
            <label className="field cs-inline">
              <span>Add a text for</span>
              <select
                value=""
                aria-label="Add a text for an account"
                onChange={(e) => {
                  const acc = e.target.value;
                  if (acc)
                    setDrafts({
                      ...drafts,
                      [acc]: { text: '', tags: '', language: d.project.language || 'en', url: '', at: '', version: null, dirty: true, msg: '', err: '', instruction: '' },
                    });
                }}
              >
                <option value="">choose an account…</option>
                {free.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
          )
        }
      >
        <p className="muted small">
          One text per publishing account, for instance the company page and, apart, your personal reshare. Accounts are edited in Content Studio → Brand &amp; templates.
        </p>
        {shown.length === 0 && <Empty>No text yet. Choose an account above.</Empty>}
        <div className="stack">
          {shown.map((acc) => {
            const a = accountOf(acc);
            const x = drafts[acc];
            const n = chars(x.text);
            const jobs = d.jobs.filter((j) => j.account_id === acc);
            const latest = jobs[0];
            const sugg = d.suggestions.filter((s) => s.account_id === acc).slice(0, 3);
            return (
              <article key={acc} className="cs-text" data-account={acc} aria-label={`Text for ${a.label}`}>
                <header className="row gap center wrap">
                  <h3>{a.label}</h3>
                  <span className="muted small">
                    {a.channel} · {a.kind} account
                  </span>
                  {x.version === null && <span className="small warn-text">not saved yet</span>}
                  {x.dirty && x.version !== null && <span className="small warn-text">unsaved changes</span>}
                </header>
                <label className="field">
                  <span>Text</span>
                  <textarea rows={7} value={x.text} onChange={(e) => setDraft(acc, { text: e.target.value, dirty: true, msg: '' })} aria-label={`Text for ${a.label}`} />
                </label>
                <p className={`small num ${n > MAX_TEXT ? 'err-text' : 'muted'}`} aria-live="polite">
                  {n} / {MAX_TEXT} characters
                </p>
                <div className="form-grid">
                  <label className="field">
                    <span>Hashtags (separated by spaces or commas)</span>
                    <input value={x.tags} onChange={(e) => setDraft(acc, { tags: e.target.value, dirty: true })} aria-label={`Hashtags for ${a.label}`} />
                  </label>
                  <label className="field">
                    <span>Language</span>
                    <select value={x.language} onChange={(e) => setDraft(acc, { language: e.target.value, dirty: true })}>
                      <option value="en">English</option>
                      <option value="fr">French</option>
                      <option value="bg">Bulgarian</option>
                    </select>
                  </label>
                  <label className="field">
                    <span>Published at (link, once it is out)</span>
                    <input type="url" value={x.url} onChange={(e) => setDraft(acc, { url: e.target.value, dirty: true })} placeholder="https://…" aria-label={`Published URL for ${a.label}`} />
                  </label>
                  <label className="field">
                    <span>Published on</span>
                    <input type="date" value={x.at} onChange={(e) => setDraft(acc, { at: e.target.value, dirty: true })} />
                  </label>
                </div>
                <div className="row gap center wrap">
                  <button type="button" className="btn primary small" onClick={() => void saveText(acc)} disabled={n > MAX_TEXT}>
                    Save text
                  </button>
                  <CopyButton value={x.text} label="Copy text" />
                  <CopyButton value={x.tags.trim() ? `${x.text}\n\n${parseTags(x.tags).map((t) => (t.startsWith('#') ? t : `#${t}`)).join(' ')}` : x.text} label="Copy text with hashtags" />
                  <button type="button" className="btn ghost small" onClick={() => void removeText(acc)}>
                    Remove this text
                  </button>
                  {x.msg && (
                    <span className="ok-text small" role="status">
                      {x.msg}
                    </span>
                  )}
                </div>
                <ErrorMsg>{x.err}</ErrorMsg>

                <div className="cs-suggest">
                  <h4>Suggestion from the model</h4>
                  <div className="row gap center wrap">
                    <label className="field grow">
                      <span>Extra instruction (optional, 500 characters)</span>
                      <input value={x.instruction} maxLength={500} onChange={(e) => setDraft(acc, { instruction: e.target.value })} aria-label={`Instruction for ${a.label}`} />
                    </label>
                    <button type="button" className="btn small" onClick={() => void suggest(acc)} disabled={!d.model_jobs.enabled}>
                      Suggest with the model
                    </button>
                  </div>
                  {!d.model_jobs.enabled && <p className="msg warn">{d.model_jobs.reason} Set one up in Settings → Model policy.</p>}
                  {latest && (latest.status === 'queued' || latest.status === 'running') && (
                    <p className="msg info" role="status" data-job={latest.status}>
                      {latest.status === 'running'
                        ? 'The model is writing the suggestion…'
                        : d.model_jobs.agent_recently_seen
                          ? 'Queued: an agent is connected and will pick it up.'
                          : 'Queued, waiting for an agent: no agent is connected right now. The suggestion appears here once the GPU worker runs.'}
                    </p>
                  )}
                  {latest && latest.status === 'failed' && (
                    <p className="msg err" role="alert">
                      The last suggestion failed: {latest.error ?? 'no reason given'}
                    </p>
                  )}
                  {sugg.map((s) => (
                    <div key={s.id} className="cs-suggestion" data-suggestion={s.id}>
                      <p className="cs-suggestion-text">{s.text}</p>
                      {s.hashtags.length > 0 && <p className="small mono">{s.hashtags.join(' ')}</p>}
                      {s.warnings.map((w) => (
                        <p key={w} className="msg warn">
                          {w}
                        </p>
                      ))}
                      <div className="row gap center">
                        <button type="button" className="btn small" onClick={() => applySuggestion(acc, s)}>
                          Use this
                        </button>
                        <span className="muted small">
                          {s.model?.model ?? s.model?.provider ?? 'model'}
                          {s.model?.is_local ? ' (local)' : ''} · a proposal, check the facts before publishing
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </Section>

      <Section title="Delete">
        {confirmDelete ? (
          <div className="row gap center">
            <span className="small">Delete this publication, its texts and its links to files? The stored files are kept on the server.</span>
            <button type="button" className="btn danger small" onClick={() => void deletePublication()}>
              Yes, delete
            </button>
            <button type="button" className="btn ghost small" onClick={() => setConfirmDelete(false)}>
              Keep it
            </button>
          </div>
        ) : (
          <button type="button" className="btn ghost small" onClick={() => setConfirmDelete(true)}>
            Delete this publication
          </button>
        )}
      </Section>
    </div>
  );
}
